// ── Button — кнопка с текстом ────────────────────────────────────────────────
// Варианты: primary (синяя), secondary (с рамкой), ghost (без рамки, синий текст),
// soft (бледно-синяя подложка — «выбрано»). danger — красная. В теме с фишками («Колода»)
// primary — покерная фишка-плашка (chipFace в surface.js).
// Размеры: sm 32 · md 40 · lg 48 px по высоте; на сенсорном экране у sm зона
// нажатия невидимо расширена до 44 px (класс ac-hit в index.css).
//
// Длинная надпись (испанский, немецкий) переносится на вторую строку, а не
// вылезает за кнопку. nowrap — одна строка с «…» и подсказкой при наведении.

import { forwardRef } from 'react';
import Icon from '../Icon';
import { SANS } from '../themes';
import { labelOn, shade } from './color';
import { chipFace } from './surface';
import { usePalette } from './theme';

const H = { sm: 32, md: 40, lg: 48 };
const PAD = { sm: '4px 12px', md: '6px 16px', lg: '8px 20px' };
const FS = { sm: 13, md: 14, lg: 15.5 };
const PAD_CHIP = { sm: '4px 16px', md: '6px 22px', lg: '8px 26px' }; // пунктир вставок — не впритык к надписи

export function buttonColors(C, variant, danger) {
  // Заливка опасной кнопки и надпись на цветных — из темы, если она их задаёт (src/themes.js).
  const accent = danger ? (C.dangerFill || C.danger) : C.blue;
  switch (variant) {
    case 'primary': { const l = labelOn(C, accent); return { bg: accent, fg: l.fg, bd: accent, outline: l.outline }; }
    case 'ghost': return { bg: 'transparent', fg: danger ? C.danger : C.blueDark, bd: 'transparent' };
    case 'soft': return { bg: danger ? 'transparent' : C.bluePale, fg: danger ? C.danger : C.blueDark, bd: danger ? C.danger : C.bluePale };
    default: return { bg: C.card, fg: danger ? C.danger : C.text, bd: C.border };
  }
}

const Button = forwardRef(function Button({
  variant = 'secondary', size = 'md', danger = false, icon, iconRight, full = false, nowrap = false,
  disabled = false, busy = false, children, style, className = '', title, C: Cp, type = 'button', ...rest
}, ref) {
  const C = usePalette(Cp);
  const col = buttonColors(C, variant, danger);
  const label = typeof children === 'string' ? children : undefined;
  // Объёмная тема (lift в src/themes.js): у кнопки с заливкой — тёмное «ребро» снизу
  // своего же цвета (правила [data-lift] в index.css); прозрачные (ghost) — без него.
  const lift = !!C.lift && col.bg !== 'transparent';
  const chip = variant === 'primary' ? chipFace(C, { danger }) : null;
  return (
    <button ref={ref} type={type} disabled={disabled || busy} aria-busy={busy || undefined}
      title={title || (nowrap ? label : undefined)}
      data-variant={variant} data-lift={lift && !chip ? '' : undefined} data-outline={lift && !chip && col.outline ? '' : undefined} {...chip?.attrs}
      className={['ac-btn', variant === 'primary' ? 'ac-press' : 'ac-ibtn', size === 'sm' ? 'ac-hit' : '', className].filter(Boolean).join(' ')}
      style={{
        display: full ? 'flex' : 'inline-flex', width: full ? '100%' : undefined, maxWidth: '100%', minWidth: 0,
        minHeight: H[size], padding: PAD[size], gap: 8, alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
        borderRadius: size === 'lg' ? 10 : 8, border: `${C.borderW || 1}px solid ${col.bd}`, background: col.bg, color: col.fg,
        '--ac-edge': lift ? shade(col.bg) : undefined,
        // Главная кнопка в объёмной теме ещё и светится своим цветом.
        '--ac-glow': lift && variant === 'primary' ? `${col.bg}99` : undefined,
        fontFamily: SANS, fontSize: FS[size], fontWeight: variant === 'primary' ? 600 : 500, lineHeight: 1.25,
        textAlign: 'center', cursor: disabled || busy ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
        flexShrink: 1, ...(chip ? { ...chip.style, padding: PAD_CHIP[size] } : null), ...style,
      }}
      {...rest}>
      {icon && <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon n={busy ? 'refresh' : icon} s={size === 'sm' ? 15 : 17} /></span>}
      {children != null && children !== false && (
        <span style={nowrap
          ? { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
          : { minWidth: 0, overflowWrap: 'break-word' }}>{children}</span>
      )}
      {iconRight && <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon n={iconRight} s={size === 'sm' ? 15 : 17} /></span>}
    </button>
  );
});

export default Button;
